import {
  Component,
  Injector,
  Input,
  OnInit,
  ChangeDetectorRef,
  inject,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { DialogService } from '@nrwl/dialog-service';
import { RxDialogComponent } from '@nrwl/rx-dialog';
import { FormsDialogConfigService } from '@nrwl/dialog-configs';
import { AuthService } from '@nrwl/rx-service-auth';
import { SoapAttributesDialogComponent } from './components/dialogs/soap-attributes-dialog.component';
import { SoapPlanItemsDialogComponent } from './components/dialogs/soap-plan-items-dialog.component';
import { HttpClient } from '@angular/common/http';
import { RxIconModule } from '@nrwl/rx-icon';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { parseLocalDateString, toLocalDateTimeString } from '../../../helpers/date-utils';
import { WidgetBase } from '../../../models/widget-base';
import { FbEmrSoapNotesWidget } from '../../../models/form-widget-emr-soap-notes.model';
import {
  FbSoapInputListComponent,
  InputField,
  SoapInputRow,
} from './components/fb-soap-input-list';
import {
  SoapAttribute,
  SoapAttributeSelection,
  SoapCategory,
  SoapPlanItem,
  SoapAttributesByCategory,
  SoapNotesService,
  SoapNotesPayload,
  normalizePlanItemLabel,
  resolvePlanItemName,
} from './soap-notes.service';
import { SoapNotesScribeSupportService } from './soap-notes-scribe-support.service';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { environment } from '@nrwl/environment';
import { EmrReadonlyNotice } from '../../../helpers/components/emr-readonly-notice';
import { RX_APP_CONTEXT, RxAppContext } from '@nrwl/rx-root-state';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';
import { copyScribeSuggestionMarker } from '../../scribe-suggestion-marker.util';

const SOAP_CATEGORY_BY_SECTION: Record<
  'subjective' | 'objective' | 'assessment' | 'plan',
  SoapCategory
> = {
  subjective: 'S',
  objective: 'O',
  assessment: 'A',
  plan: 'P',
};

@Component({
  selector: 'fb-emr-soap-notes',
  standalone: true,
  providers: [SoapNotesScribeSupportService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDividerModule,
    RxIconModule,
    TokenToDivPipe,
    FbSoapInputListComponent,
    EmrReadonlyNotice,
  ],
  templateUrl: './fb-emr-soap-notes.component.html',
  styleUrls: ['./fb-emr-soap-notes.component.scss'],
})
export class FbEmrSoapNotesComponent extends WidgetBase implements OnInit {
  @Input() public isTemplatePrint = false;
  private readonly sectionExpandedOverrides: Partial<
    Record<'subjective' | 'objective' | 'assessment' | 'plan', boolean>
  > = {};
  private authService = inject(AuthService);
  private soapNotesLoading = false;
  private soapNotesLoaded = false;
  private _treatmentId: string | number | null = null;
  private attributeLabelsReady: Record<
    'subjective' | 'objective' | 'assessment' | 'plan',
    boolean
  > = {
    subjective: false,
    objective: false,
    assessment: false,
    plan: false,
  };

  public soapForm: FormGroup;
  subjectiveAttributes: string[] = [];
  objectiveAttributes: string[] = [];
  assessmentAttributes: string[] = [];
  planAttributes: string[] = [];
  subjectiveAttributeLabels: string[] = [];
  objectiveAttributeLabels: string[] = [];
  assessmentAttributeLabels: string[] = [];
  planAttributeLabels: string[] = [];
  planItems: SoapPlanItem[] = [];
  planItemsQuantity: unknown[] | number | string | null = null;
  defaultDateValue = '';
  defaultEmployeeId = '';
  isFillAndSignMode = false;
  employees$ = new BehaviorSubject<Array<{ text: string; value: string }>>([]);
  private employeesLoaded = false;
  private isLoadingEmployees = false;
  private employeesPage = 1;
  private readonly employeesPageSize = 200;
  private employeesHasMore = true;
  private employeesCache: Array<{ text: string; value: string }> = [];
  private isApplyingDateUpdate = false;

  subjectiveFields: InputField[] = [
    { title: 'Date', value: '', type: 'date' },
    { title: 'Employee', value: '', type: 'employee' },
    { title: 'Priority', value: '', type: 'priority' },
    { title: '', value: '', type: 'textarea', placeholder: 'Enter subjective note' },
  ];

  objectiveFields: InputField[] = [
    { title: 'Date', value: '', type: 'date' },
    { title: 'Employee', value: '', type: 'employee' },
    { title: 'Priority', value: '', type: 'priority' },
    { title: '', value: '', type: 'textarea', placeholder: 'Enter objective note' },
  ];

  assessmentFields: InputField[] = [
    { title: 'Date', value: '', type: 'date' },
    { title: 'Employee', value: '', type: 'employee' },
    { title: 'Priority', value: '', type: 'priority' },
    { title: '', value: '', type: 'textarea', placeholder: 'Enter assessment note' },
  ];

  planFields: InputField[] = [
    { title: 'Date', value: '', type: 'date' },
    { title: 'Employee', value: '', type: 'employee' },
    { title: 'Priority', value: '', type: 'priority' },
    { title: 'Plan Fields', value: '', type: 'plan-fields', placeholder: 'Enter plan fields' },
    {
      title: 'Plan Items',
      value: '',
      type: 'plan-items',
      placeholder: 'Enter plan items',
    },
    {
      title: 'Plan Items Quantity',
      value: '',
      type: 'plan-items-quantity',
      placeholder: 'Enter plan items quantity',
    },
    { title: '', value: '', type: 'textarea', placeholder: 'Enter plan note' },
  ];

  constructor(
    private injector: Injector,
    private cd: ChangeDetectorRef,
    private soapNotesService: SoapNotesService,
    private formBuilder: FormBuilder,
    private http: HttpClient,
    private dialogService: DialogService,
    private formsDialogConfigService: FormsDialogConfigService,
    private soapNotesScribeSupport: SoapNotesScribeSupportService,
    @Inject(RX_APP_CONTEXT)
    private appContext: RxAppContext,
  ) {
    super(injector);
    this.soapForm = this.formBuilder.group({
      subjective: [[]],
      objective: [[]],
      assessment: [[]],
      plan: [[]],
    });
  }

  private get isRxStaffContext(): boolean {
    return this.appContext === RxAppContext.RxStaff;
  }

  /**
   * True while a patient's own plan is being customized. The widget then draws its empty default
   * look and never reads or writes the saved notes, so they are stored back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  ngOnInit() {
    this.isFillAndSignMode = this.getFillAndSignMode();
    this.updateDefaultDateValue();
    this.initializeData();
    if (!this.isRxStaffContext) {
      this.ensureSoapAttributesCatalogLoaded();
      this.ensureSoapPlanItemsCatalogLoaded();
    }
    if (this.isFillAndSignMode && !this.isEmrReadonly) {
      this.resolveActiveEmployee();
    }
    if (this.isPrinting || this.isEmrReadonly) {
      this.employeesLoaded = true;
    } else {
      this.loadEmployees();
    }
    this.soapForm.valueChanges.subscribe(() => {
      this.applyCurrentDateToChangedNotesInForm();
      this.updatePayload();
      this.cd.markForCheck();
    });
  }

  @Input()
  public override set payload(value) {
    this._payload = value;
    this.isFillAndSignMode = this.getFillAndSignMode();
    this.updateDefaultDateValue();
    this.initializeData();
    this.tryLoadSoapNotes();
    this.ready.next(true);
  }
  public override get payload() {
    return this._payload;
  }

  @Input()
  public override set entity(value) {
    this._entity = value;
    this.tryLoadSoapNotes();
  }
  public override get entity() {
    return this._entity;
  }

  @Input()
  public override set patientId(value) {
    this._patientId = value;
    this.tryLoadSoapNotes();
  }
  public override get patientId() {
    return this._patientId;
  }

  @Input()
  public override set patient(value) {
    this._patient = value;
    this.tryLoadSoapNotes();
  }
  public override get patient() {
    return this._patient;
  }

  @Input()
  public override set appointment(value) {
    this._appointment = value;
    this.tryLoadSoapNotes();
  }
  public override get appointment() {
    return this._appointment;
  }

  @Input()
  public override set isPreview(value: boolean) {
    super.isPreview = value;
    this.isFillAndSignMode = this.getFillAndSignMode();
    this.updateDefaultDateValue();
    this.initializeData();
    this.tryLoadSoapNotes();
  }
  public override get isPreview() {
    return super.isPreview;
  }

  @Input()
  public override set isDesign(value: boolean) {
    super.isDesign = value;
    this.isFillAndSignMode = this.getFillAndSignMode();
    this.updateDefaultDateValue();
    this.initializeData();
    this.tryLoadSoapNotes();
  }
  public override get isDesign() {
    return super.isDesign;
  }

  @Input()
  public override set isPrinting(value: boolean) {
    super.isPrinting = value;
    this.isFillAndSignMode = this.getFillAndSignMode();
    this.updateDefaultDateValue();
    this.initializeData();
  }
  public override get isPrinting() {
    return super.isPrinting;
  }

  @Input()
  public override set isEmrReadonly(value: boolean) {
    super.isEmrReadonly = value;
    this.isFillAndSignMode = this.getFillAndSignMode();
    this.updateDefaultDateValue();
    this.initializeData();
    this.tryLoadSoapNotes();
  }
  public override get isEmrReadonly() {
    return super.isEmrReadonly;
  }

  @Input()
  public set treatmentId(value) {
    this._treatmentId = value;
    this.tryLoadSoapNotes();
  }
  public get treatmentId() {
    return this._treatmentId;
  }

  private tryLoadSoapNotes() {
    if (this.soapNotesLoaded || this.soapNotesLoading) {
      return;
    }

    if (this.isRxStaffContext) {
      return;
    }

    if (this.isPrinting) {
      return;
    }

    if (this.isEmrReadonly) {
      return;
    }

    if (this.isDesign) {
      return;
    }

    const isTemplate = this.isTemplateEntity();
    if (isTemplate && this.isPreview) {
      return;
    }

    if (!this.payload?.properties) {
      return;
    }

    // Guarded by flags to prevent overlapping requests during rapid input updates.
    const hasSoapNotes = this.hasExistingSoapNotes();
    const items = this.payload?.properties?.items?.value;
    const needsPlanItemsFetch = this.shouldFetchPlanItems(items);

    const shouldFetchTemplateFillAndSign = isTemplate && this.getFillAndSignMode();
    if (!shouldFetchTemplateFillAndSign && hasSoapNotes && !needsPlanItemsFetch) {
      return;
    }

    const patientId = this.patient?.remote_id;
    const treatmentId = this.getTreatmentId();

    if (!patientId || !treatmentId) {
      return;
    }

    this.soapNotesLoading = true;

    if (!shouldFetchTemplateFillAndSign && hasSoapNotes && needsPlanItemsFetch) {
      this.soapNotesService
        .getPlanItemsForTreatment(String(treatmentId))
        .pipe(
          take(1),
          catchError(() => of([] as SoapPlanItem[])),
        )
        .subscribe((planItems) => {
          this.soapNotesLoading = false;
          this.soapNotesLoaded = true;
          if (!planItems.length) {
            return;
          }
          this.applyPlanItems(planItems);
          this.cd.markForCheck();
        });
      return;
    }

    const notes$ = this.soapNotesService.getSoapNotes({
      customerId: String(patientId),
      treatmentId: String(treatmentId),
    });
    const attributes$ = this.soapNotesService.getSoapAttributes({
      customerId: String(patientId),
      treatmentId: String(treatmentId),
    });
    const planItems$ = this.soapNotesService
      .getPlanItemsForTreatment(String(treatmentId))
      .pipe(catchError(() => of([] as SoapPlanItem[])));

    forkJoin({ notes: notes$, attributes: attributes$, planItems: planItems$ })
      .pipe(
        take(1),
        catchError(() =>
          of({
            notes: this.emptySoapNotes(),
            attributes: this.emptySoapAttributes(),
            planItems: [] as SoapPlanItem[],
          }),
        ),
      )
      .subscribe(({ notes, attributes, planItems }) => {
        this.soapNotesLoading = false;
        this.soapNotesLoaded = true;

        if (
          !this.hasSoapNotesData(notes) &&
          !this.hasSoapAttributesData(attributes) &&
          planItems.length === 0
        ) {
          return;
        }

        this.applySoapNotesPayload(notes, attributes, planItems);
        this.cd.markForCheck();
      });
  }

  private loadEmployees() {
    if (this.isLoadingEmployees || this.employeesLoaded) {
      return;
    }

    if (this.isPrinting || this.isEmrReadonly) {
      this.employeesLoaded = true;
      return;
    }

    this.employeesPage = 1;
    this.employeesHasMore = true;
    this.employeesCache = [];
    this.loadEmployeesPage();
  }

  private loadEmployeesPage() {
    if (this.isLoadingEmployees || !this.employeesHasMore || this.isRxStaffContext) {
      return;
    }

    this.isLoadingEmployees = true;

    const params = new URLSearchParams();
    params.append('Page', this.employeesPage.toString());
    params.append('Rows', this.employeesPageSize.toString());
    params.append('IsActive', 'true');

    const url = `${environment.baseAPIUrlMicro}egress-proxy/pne/employees?${params.toString()}`;

    this.http
      .get<any>(url, { observe: 'response' })
      .pipe(
        take(1),
        map((response) => {
          const data = response.body?.Data || response.body || [];
          const employees = Array.isArray(data)
            ? data.map((emp: any) => this.toEmployeeOption(emp))
            : [];
          return {
            employees,
            totalPages: this.soapNotesService.getTotalPages(
              response.headers,
              this.employeesPageSize,
              employees.length,
            ),
          };
        }),
        catchError(() => of({ employees: [], totalPages: null })),
      )
      .subscribe(({ employees, totalPages }) => {
        this.employeesCache = this.mergeEmployees(this.employeesCache, employees);
        this.employees$.next(this.employeesCache);

        if (typeof totalPages === 'number' && totalPages > 0) {
          this.employeesHasMore = this.employeesPage < totalPages;
        } else {
          this.employeesHasMore = employees.length >= this.employeesPageSize;
        }

        this.employeesPage += 1;
        this.isLoadingEmployees = false;
        this.employeesLoaded = !this.employeesHasMore;
        this.cd.markForCheck();

        if (this.employeesHasMore) {
          this.loadEmployeesPage();
        }
      });
  }

  public hasSectionData(sectionName: string): boolean {
    if (this.isTemplatePrint) {
      return true;
    }

    const section = sectionName as 'subjective' | 'objective' | 'assessment' | 'plan';
    const value = this.soapForm?.get(sectionName)?.value;
    const hasMeaningfulRows =
      Array.isArray(value) && value.some((row) => this.hasRowMeaningfulContent(row));
    if (hasMeaningfulRows) {
      return true;
    }

    if (this.getAssignedAttributes(section).length > 0) {
      return true;
    }

    if (section === 'plan') {
      return (
        this.planItems.length > 0 ||
        (this.planItemsQuantity !== null &&
          this.planItemsQuantity !== undefined &&
          this.planItemsQuantity !== '')
      );
    }

    return false;
  }

  private initializeData() {
    if (!this.payload?.properties) return;

    if (this.isInstanceDesign) {
      this.initializeDefaultDesign();
      return;
    }

    const properties = this.payload.properties;
    const isTemplate = this.isTemplateEntity();
    const isTemplateReadOnly = isTemplate && (this.isPreview || this.isDesign);
    let items = properties.items?.value;
    const metadataItems = this.getSoapNotesFromMetadata();
    if (isTemplateReadOnly) {
      items = undefined;
    } else if ((!items || !this.hasSoapNotesItems(items)) && metadataItems) {
      items = metadataItems;
      properties.items = { value: metadataItems };
    } else if (items && metadataItems) {
      const mergedItems = this.mergeNoteIdsFromMetadata(items, metadataItems);
      items = mergedItems;
      properties.items = { value: mergedItems };
    }
    this.subjectiveAttributes = [];
    this.objectiveAttributes = [];
    this.assessmentAttributes = [];
    this.planAttributes = [];
    this.subjectiveAttributeLabels = [];
    this.objectiveAttributeLabels = [];
    this.assessmentAttributeLabels = [];
    this.planAttributeLabels = [];
    this.planItems = [];
    this.planItemsQuantity = null;

    const subjective = this.ensureSectionValue(
      this.mapDataToFields(items?.subjective, this.subjectiveFields),
      this.subjectiveFields,
    );
    this.subjectiveAttributes = this.getSectionAttributes(items, 'subjective');
    const subjectiveLabels = this.getStoredAttributeLabels(items, 'subjective');
    this.subjectiveAttributeLabels = subjectiveLabels.labels ?? [...this.subjectiveAttributes];
    this.attributeLabelsReady.subjective = subjectiveLabels.hasStored;
    const objective = this.ensureSectionValue(
      this.mapDataToFields(items?.objective, this.objectiveFields),
      this.objectiveFields,
    );
    this.objectiveAttributes = this.getSectionAttributes(items, 'objective');
    const objectiveLabels = this.getStoredAttributeLabels(items, 'objective');
    this.objectiveAttributeLabels = objectiveLabels.labels ?? [...this.objectiveAttributes];
    this.attributeLabelsReady.objective = objectiveLabels.hasStored;
    const assessment = this.ensureSectionValue(
      this.mapDataToFields(items?.assessment, this.assessmentFields),
      this.assessmentFields,
    );
    this.assessmentAttributes = this.getSectionAttributes(items, 'assessment');
    const assessmentLabels = this.getStoredAttributeLabels(items, 'assessment');
    this.assessmentAttributeLabels = assessmentLabels.labels ?? [...this.assessmentAttributes];
    this.attributeLabelsReady.assessment = assessmentLabels.hasStored;
    const plan = this.ensureSectionValue(
      this.mapDataToFields(items?.plan, this.planFields),
      this.planFields,
    );
    this.planAttributes = this.getSectionAttributes(items, 'plan');
    const planLabels = this.getStoredAttributeLabels(items, 'plan');
    this.planAttributeLabels = planLabels.labels ?? [...this.planAttributes];
    this.attributeLabelsReady.plan = planLabels.hasStored;
    this.planItems = this.withCatalogLabels(this.getSectionPlanItems(items));
    this.planItemsQuantity = this.getSectionPlanItemsQuantity(items);

    if (!properties.items) {
      properties.items = { value: {} };
    }

    this.soapForm.patchValue(
      {
        subjective: this.applyAutofillDefaults(subjective),
        objective: this.applyAutofillDefaults(objective),
        assessment: this.applyAutofillDefaults(assessment),
        plan: this.applyAutofillDefaults(plan),
      },
      { emitEvent: false },
    );

    if (!this.isPrinting) {
      this.mergeEmployeeLabels(items);
      this.ensureSelectedEmployees(items);
    }
    if (!this.isEmrReadonly) {
      this.loadAttributeLabels();
    }
    if (!isTemplateReadOnly) {
      this.updatePayload();
    }
  }

  /** Empty rows, no attributes or plan items; the payload is left exactly as it was saved. */
  private initializeDefaultDesign(): void {
    this.subjectiveAttributes = [];
    this.objectiveAttributes = [];
    this.assessmentAttributes = [];
    this.planAttributes = [];
    this.subjectiveAttributeLabels = [];
    this.objectiveAttributeLabels = [];
    this.assessmentAttributeLabels = [];
    this.planAttributeLabels = [];
    this.attributeLabelsReady = {
      subjective: false,
      objective: false,
      assessment: false,
      plan: false,
    };
    this.planItems = [];
    this.planItemsQuantity = null;

    this.soapForm.patchValue(
      {
        subjective: this.ensureSectionValue([], this.subjectiveFields),
        objective: this.ensureSectionValue([], this.objectiveFields),
        assessment: this.ensureSectionValue([], this.assessmentFields),
        plan: this.ensureSectionValue([], this.planFields),
      },
      { emitEvent: false },
    );
    this.cd.markForCheck();
  }

  public isSectionExpanded(section: 'subjective' | 'objective' | 'assessment' | 'plan'): boolean {
    if (this.sectionExpandedOverrides[section]) {
      return true;
    }

    if (section === 'subjective') {
      return true;
    }

    return this.isDesign || this.isPrinting || (this.isPreview && this.hasSectionData(section));
  }

  private mapDataToFields(data: any[] | undefined, fields: InputField[]): SoapInputRow[] {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map((item) => {
      const rowFields = fields.map((field) => {
        let value: string | number = '';
        if (field.type === 'date') {
          value = item.date || '';
        } else if (field.type === 'employee') {
          value = item.employee || item.employee_id || '';
        } else if (field.type === 'priority') {
          const priorityValue =
            item.priority ?? item.Priority ?? item.PriorityLevel ?? item.priorityLevel;
          value = this.normalizePriorityValue(priorityValue);
        } else if (field.type === 'plan-fields') {
          value = this.formatListValue(item.plan_fields ?? item.planFields);
        } else if (field.type === 'plan-items') {
          value = this.formatListValue(item.plan_items ?? item.planItems);
        } else if (field.type === 'plan-items-quantity') {
          value = this.formatScalarValue(item.plan_items_quantity ?? item.planItemsQuantity);
        } else if (field.type === 'textarea') {
          value = item.note ?? item.Note ?? '';
        }
        return {
          ...field,
          value,
        };
      });
      const result: SoapInputRow = {
        fields: rowFields,
        id: item.id ?? item.Id,
      };
      copyScribeSuggestionMarker(item, result);
      return result;
    });
  }

  private mergeNoteIdsFromMetadata(items: any, metadataItems: any): any {
    if (
      !items ||
      typeof items !== 'object' ||
      !metadataItems ||
      typeof metadataItems !== 'object'
    ) {
      return items;
    }

    const sections = ['subjective', 'objective', 'assessment', 'plan'];
    let changed = false;
    const merged: any = { ...items };

    sections.forEach((section) => {
      const currentSection = Array.isArray(items[section]) ? items[section] : [];
      const metadataSection = Array.isArray(metadataItems[section]) ? metadataItems[section] : [];

      if (currentSection.length === 0 || metadataSection.length === 0) {
        return;
      }

      merged[section] = currentSection.map((item: any, index: number) => {
        if (!item || typeof item !== 'object') {
          return item;
        }

        const existingId = item.id ?? item.Id ?? item.ID;
        if (existingId !== null && existingId !== undefined && existingId !== '') {
          return item;
        }

        const metadataItem = metadataSection[index];
        const metadataId =
          metadataItem && typeof metadataItem === 'object'
            ? ((metadataItem as any).id ?? (metadataItem as any).Id ?? (metadataItem as any).ID)
            : null;
        if (metadataId !== null && metadataId !== undefined && metadataId !== '') {
          changed = true;
          return { ...item, id: metadataId };
        }

        return item;
      });
    });

    return changed ? merged : items;
  }

  private createEmptyRow(fields: InputField[]): SoapInputRow {
    return {
      fields: fields.map((field) => ({
        ...field,
        value: '',
      })),
    };
  }

  private getAttributesByType(type: 'subjective' | 'objective' | 'assessment' | 'plan'): string[] {
    if (type === 'subjective') {
      return this.subjectiveAttributes;
    }
    if (type === 'objective') {
      return this.objectiveAttributes;
    }
    if (type === 'assessment') {
      return this.assessmentAttributes;
    }
    return this.planAttributes;
  }

  private capitalize(value: string): string {
    if (!value) {
      return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  onAttributesChanged(
    type: 'subjective' | 'objective' | 'assessment' | 'plan',
    attributes: string[],
  ) {
    this.setAttributes(type, attributes, attributes, false);
    this.clearScribeSuggestionMarker(this.soapMarkerTarget, `${type}Attributes`);
    this.updatePayload();
    this.cd.markForCheck();
  }

  public get soapMarkerTarget(): ScribeSuggestionMarkerTarget | null {
    const items = this.payload?.properties?.items?.value;
    return items && typeof items === 'object' && !Array.isArray(items)
      ? (items as ScribeSuggestionMarkerTarget)
      : null;
  }

  public getSectionMarkerTargets(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
  ): ScribeSuggestionMarkerTarget[] {
    const rows = this.soapMarkerTarget?.[section];
    return Array.isArray(rows)
      ? rows.filter(
          (row): row is ScribeSuggestionMarkerTarget =>
            !!row && typeof row === 'object' && !Array.isArray(row),
        )
      : [];
  }

  public syncScribeSuggestionMarkersFromPayload(): void {
    const sections = Object.keys(SOAP_CATEGORY_BY_SECTION) as Array<
      'subjective' | 'objective' | 'assessment' | 'plan'
    >;

    sections.forEach((section) => {
      const control = this.soapForm.get(section);
      const rows = control?.value;
      if (!control || !Array.isArray(rows)) {
        return;
      }

      const markerTargets = this.getSectionMarkerTargets(section);
      const nextRows = rows.map((row: SoapInputRow, index: number) => {
        const nextRow: SoapInputRow = {
          ...row,
          fields: row.fields.map((field) => ({ ...field })),
        };
        copyScribeSuggestionMarker(markerTargets[index], nextRow);
        return nextRow;
      });

      control.setValue(nextRows, { emitEvent: false });
    });

    this.cd.markForCheck();
  }

  public getSoapAttributeOptionsSnapshot(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
  ): SoapAttributeSelection[] {
    return this.soapNotesScribeSupport.getSoapAttributeOptionsSnapshot(section);
  }

  public getSoapPlanItemOptionsSnapshot(): Array<{ id: string; name: string }> {
    return this.soapNotesScribeSupport.getSoapPlanItemOptionsSnapshot();
  }

  public applySectionAttributesSuggestion(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
    attributeIds: string[],
  ): void {
    const selectedIds = Array.isArray(attributeIds) ? attributeIds.map((id) => `${id}`) : [];
    const labels = this.soapNotesScribeSupport.resolveAttributeLabels(section, selectedIds);

    this.setAttributes(section, selectedIds, labels, true);
    this.updatePayload();
    this.sectionExpandedOverrides[section] = true;
    this.cd.markForCheck();
  }

  public applyPlanItemsSuggestion(
    planItemIds: Array<{ id: string; name?: string }> | string[],
  ): void {
    this.applyPlanItems(this.soapNotesScribeSupport.resolvePlanItemsFromSuggestion(planItemIds));
    this.sectionExpandedOverrides.plan = true;
    this.cd.markForCheck();
  }

  public applyPlanItemsQuantitySuggestion(value: unknown[] | string | null): void {
    if (!this.payload?.properties) {
      return;
    }

    if (!this.payload.properties.items) {
      this.payload.properties.items = { value: {} };
    }

    this.planItemsQuantity = value;
    const items = this.payload.properties.items.value ?? {};
    this.payload.properties.items.value = {
      ...items,
      plan_items_quantity: this.planItemsQuantity,
    };
    this.updatePayload();
    this.sectionExpandedOverrides.plan = true;
    this.cd.markForCheck();
  }

  private getSoapNotesFromMetadata(): Record<string, unknown> | null {
    const metadata = this.entity?.metadata?.soap_notes ?? this.entity?.metadata?.soapNotes ?? null;

    if (!metadata || typeof metadata !== 'object') {
      return null;
    }

    const record = metadata as Record<string, unknown>;
    const planItems = this.normalizePlanItemsValue(record['plan_items']);
    const planItemsQuantity = this.getPlanItemsQuantityFromMetadata(record['plan_items']);
    return {
      subjective: Array.isArray(record['subjective_notes']) ? record['subjective_notes'] : [],
      objective: Array.isArray(record['objective_notes']) ? record['objective_notes'] : [],
      assessment: Array.isArray(record['assessment_notes']) ? record['assessment_notes'] : [],
      plan: Array.isArray(record['plan_notes']) ? record['plan_notes'] : [],
      subjective_attributes: record['subjective_notes_attributes'] ?? [],
      objective_attributes: record['objective_notes_attributes'] ?? [],
      assessment_attributes: record['assessment_notes_attributes'] ?? [],
      plan_attributes: record['plan_notes_attributes'] ?? [],
      plan_items: planItems,
      ...(planItemsQuantity !== null ? { plan_items_quantity: planItemsQuantity } : {}),
    };
  }

  private hasSoapNotesItems(items: any): boolean {
    if (!items || typeof items !== 'object') {
      return false;
    }

    return (
      this.sectionHasData(items.subjective) ||
      this.sectionHasData(items.objective) ||
      this.sectionHasData(items.assessment) ||
      this.sectionHasData(items.plan) ||
      this.getSectionAttributes(items, 'subjective').length > 0 ||
      this.getSectionAttributes(items, 'objective').length > 0 ||
      this.getSectionAttributes(items, 'assessment').length > 0 ||
      this.getSectionAttributes(items, 'plan').length > 0 ||
      this.getSectionPlanItems(items).length > 0 ||
      this.getSectionPlanItemsQuantity(items) !== null
    );
  }

  private setAttributes(
    type: 'subjective' | 'objective' | 'assessment' | 'plan',
    ids: string[],
    labels: string[],
    labelsReady = false,
  ): void {
    if (type === 'subjective') {
      this.subjectiveAttributes = ids;
      this.subjectiveAttributeLabels = labels;
      this.attributeLabelsReady.subjective = labelsReady;
    }
    if (type === 'objective') {
      this.objectiveAttributes = ids;
      this.objectiveAttributeLabels = labels;
      this.attributeLabelsReady.objective = labelsReady;
    }
    if (type === 'assessment') {
      this.assessmentAttributes = ids;
      this.assessmentAttributeLabels = labels;
      this.attributeLabelsReady.assessment = labelsReady;
    }
    if (type === 'plan') {
      this.planAttributes = ids;
      this.planAttributeLabels = labels;
      this.attributeLabelsReady.plan = labelsReady;
    }
  }

  openAddAttributeDialog(type: 'subjective' | 'objective' | 'assessment' | 'plan'): void {
    if (this.isDesign || this.isPrinting || this.isSubmitted || this.isEmrReadonly) {
      return;
    }

    const selected = this.getAttributesByType(type);
    const title = `Add ${this.capitalize(type)} Attribute`;

    const config = {
      component: SoapAttributesDialogComponent,
      payload: {
        title,
        selected,
        soapCategory: SOAP_CATEGORY_BY_SECTION[type],
        buttons: this.formsDialogConfigService.SubmitDismissButtons('Add', 'Cancel'),
      },
    };
    this.dialogService
      .prompt(RxDialogComponent, { ...config, width: '420px' })
      .pipe(
        filter((result) => Array.isArray(result)),
        take(1),
      )
      .subscribe((result: SoapAttributeSelection[]) => {
        const ids = result.map((item) => item.id);
        const labels = result.map((item) => item.name);
        this.setAttributes(type, ids, labels, true);
        this.clearScribeSuggestionMarker(this.soapMarkerTarget, `${type}Attributes`);
        this.updatePayload();
        this.cd.markForCheck();
      });
  }

  openPlanItemsDialog(): void {
    if (this.isDesign || this.isPrinting || this.isSubmitted || this.isEmrReadonly) {
      return;
    }

    const config = {
      component: SoapPlanItemsDialogComponent,
      payload: {
        title: 'Add Plan Items',
        planItems: this.planItems,
        planItemsQuantity: this.planItemsQuantity,
        buttons: this.formsDialogConfigService.SubmitDismissButtons('Add', 'Cancel'),
      },
    };
    this.dialogService
      .prompt(RxDialogComponent, { ...config, width: '420px' })
      .pipe(
        filter((result) => !!result),
        take(1),
      )
      .subscribe((result: { items: SoapPlanItem[]; quantities: string[] }) => {
        this.planItems = Array.isArray(result.items) ? result.items : [];
        this.planItemsQuantity = Array.isArray(result.quantities) ? result.quantities : [];
        this.clearScribeSuggestionMarker(this.soapMarkerTarget, 'planItems');
        this.clearScribeSuggestionMarker(this.soapMarkerTarget, 'planItemsQuantity');
        this.updatePayload();
        this.cd.markForCheck();
      });
  }

  private getTreatmentId(): string | null {
    const treatmentId =
      this._treatmentId ?? this.appointment?.TreatmentId ?? this.entity?.metadata?.treatment_id;
    return treatmentId ? String(treatmentId) : null;
  }

  private updatePayload() {
    if (this.isInstanceDesign) {
      return;
    }

    if (this.payload?.properties?.items) {
      const formValue = this.soapForm.getRawValue();
      const nextItems: Record<string, unknown> = {
        subjective: this.formatDataForPayload(
          formValue.subjective || [],
          this.subjectiveAttributes,
        ),
        objective: this.formatDataForPayload(formValue.objective || [], this.objectiveAttributes),
        assessment: this.formatDataForPayload(
          formValue.assessment || [],
          this.assessmentAttributes,
        ),
        plan: this.formatDataForPayload(
          formValue.plan || [],
          this.planAttributes,
          this.planItems,
          this.planItemsQuantity,
        ),
        subjective_attributes: [...this.subjectiveAttributes],
        objective_attributes: [...this.objectiveAttributes],
        assessment_attributes: [...this.assessmentAttributes],
        plan_attributes: [...this.planAttributes],
        ...(this.attributeLabelsReady.subjective && this.subjectiveAttributeLabels.length
          ? { subjective_attribute_labels: [...this.subjectiveAttributeLabels] }
          : {}),
        ...(this.attributeLabelsReady.objective && this.objectiveAttributeLabels.length
          ? { objective_attribute_labels: [...this.objectiveAttributeLabels] }
          : {}),
        ...(this.attributeLabelsReady.assessment && this.assessmentAttributeLabels.length
          ? { assessment_attribute_labels: [...this.assessmentAttributeLabels] }
          : {}),
        ...(this.attributeLabelsReady.plan && this.planAttributeLabels.length
          ? { plan_attribute_labels: [...this.planAttributeLabels] }
          : {}),
        plan_items: [...this.planItems],
        plan_items_quantity: this.planItemsQuantity,
      };

      const selectedEmployeeIds = this.collectEmployeeIds(nextItems);
      const storedLabels = this.getStoredEmployeeLabels(this.payload?.properties?.items?.value);
      const labelMap = new Map(storedLabels.map((item) => [item.value, item.text]));
      const resolvedLabels = this.buildEmployeeLabels(selectedEmployeeIds);
      resolvedLabels.forEach((item) => labelMap.set(item.value, item.text));
      const employeeLabels = selectedEmployeeIds
        .map((id) => {
          const key = String(id);
          const text = labelMap.get(key);
          return text ? { value: key, text } : null;
        })
        .filter((item): item is { text: string; value: string } => !!item);
      if (employeeLabels.length > 0) {
        nextItems['employee_labels'] = employeeLabels;
      }

      copyScribeSuggestionMarker(this.soapMarkerTarget, nextItems);
      this.payload.properties.items.value = nextItems;
    }
  }

  private formatDataForPayload(
    notes: SoapInputRow[],
    assignedAttributes: string[],
    planItems?: SoapPlanItem[],
    planItemsQuantity?: unknown[] | number | string | null,
  ): any[] {
    return notes.map((row) => {
      const result: any = {};
      if (row.id !== null && row.id !== undefined && row.id !== '') {
        result.id = row.id;
      }
      const hasSectionContent =
        assignedAttributes.length > 0 ||
        (planItems && planItems.length > 0) ||
        (planItemsQuantity !== null && planItemsQuantity !== undefined && planItemsQuantity !== '');
      const hasRowContent = this.hasRowMeaningfulContent(row) || hasSectionContent;
      row.fields.forEach((field) => {
        if (field.type === 'date') {
          if (!this.isDefaultAutofillField(field, field.value) || hasRowContent) {
            result.date = toLocalDateTimeString(field.value);
          }
        } else if (field.type === 'employee') {
          if (!this.isDefaultAutofillField(field, field.value) || hasRowContent) {
            result.employee = field.value || '';
          }
        } else if (field.type === 'priority') {
          result.priority = this.normalizePriorityForPayload(field.value);
        } else if (field.type === 'plan-fields') {
          result.plan_fields = this.parseListValue(field.value);
        } else if (field.type === 'plan-items') {
          const manualItems = this.parseListValue(field.value)
            .map((item) => this.toPlanItem(item))
            .filter((item): item is SoapPlanItem => !!item);
          if (manualItems.length > 0) {
            result.plan_items = manualItems;
          }
        } else if (field.type === 'plan-items-quantity') {
          result.plan_items_quantity = field.value ?? '';
        } else if (field.type === 'textarea') {
          result.note = field.value || '';
        }
      });
      if (assignedAttributes.length > 0) {
        result.assigned_attributes = [...assignedAttributes];
      }
      if (planItems && planItems.length > 0) {
        result.plan_items = [...planItems];
      }
      if (
        planItemsQuantity !== null &&
        planItemsQuantity !== undefined &&
        planItemsQuantity !== ''
      ) {
        result.plan_items_quantity = planItemsQuantity;
      }
      copyScribeSuggestionMarker(row, result);
      return result;
    });
  }

  override isValid(): boolean {
    if (this.isEmrReadonly) {
      return true;
    }

    if (!this.payload?.properties?.required?.value) {
      return true;
    }

    return (
      this.hasValidData(this.getSectionValue('subjective'), this.subjectiveAttributes) ||
      this.hasValidData(this.getSectionValue('objective'), this.objectiveAttributes) ||
      this.hasValidData(this.getSectionValue('assessment'), this.assessmentAttributes) ||
      this.hasValidData(
        this.getSectionValue('plan'),
        this.planAttributes,
        this.planItems,
        this.planItemsQuantity,
      )
    );
  }

  private hasValidData(
    notes: SoapInputRow[],
    attributes: string[],
    planItems?: SoapPlanItem[],
    planItemsQuantity?: unknown[] | number | string | null,
  ): boolean {
    return (
      notes.some((row) => this.hasRowMeaningfulContent(row)) ||
      attributes.length > 0 ||
      !!(planItems && planItems.length > 0) ||
      (planItemsQuantity !== null && planItemsQuantity !== undefined && planItemsQuantity !== '')
    );
  }

  private normalizeAttributes(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter((item) => item);
    }

    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }

    return [];
  }

  private applyAutofillDefaults(rows: SoapInputRow[]): SoapInputRow[] {
    if (!this.isFillAndSignMode) {
      return rows;
    }

    const hasDateDefault = !!this.defaultDateValue;
    const hasEmployeeDefault = !!this.defaultEmployeeId;
    if (!hasDateDefault && !hasEmployeeDefault) {
      return rows;
    }

    const nextRows = rows.map((row) => ({
      ...row,
      fields: row.fields.map((field) => {
        if (
          field.type === 'date' &&
          hasDateDefault &&
          (!field.value || this.shouldResetTemplateShellDate(row))
        ) {
          return { ...field, value: this.defaultDateValue };
        }
        if (!field.value && field.type === 'employee' && hasEmployeeDefault) {
          return { ...field, value: this.defaultEmployeeId };
        }
        return field;
      }),
    }));

    return nextRows;
  }

  private shouldResetTemplateShellDate(row: SoapInputRow): boolean {
    if (!this.isTemplateEntity() || !this.isFillAndSignMode) {
      return false;
    }

    if (!row || !Array.isArray(row.fields)) {
      return false;
    }

    if (row.id !== null && row.id !== undefined && row.id !== '') {
      return false;
    }

    const hasMeaningfulContent = this.hasRowMeaningfulContent(row);
    if (hasMeaningfulContent) {
      return false;
    }

    const employeeValue = row.fields.find((field) => field.type === 'employee')?.value;
    return !this.isMeaningfulValue(employeeValue);
  }

  private applyAutofillDefaultsToForm(): void {
    if (!this.isFillAndSignMode) {
      return;
    }

    this.soapForm.patchValue(
      {
        subjective: this.applyAutofillDefaults(this.getSectionValue('subjective')),
        objective: this.applyAutofillDefaults(this.getSectionValue('objective')),
        assessment: this.applyAutofillDefaults(this.getSectionValue('assessment')),
        plan: this.applyAutofillDefaults(this.getSectionValue('plan')),
      },
      { emitEvent: false },
    );
  }

  private resolveActiveEmployee(): void {
    if (this.isEmrReadonly || this.isRxStaffContext) {
      return;
    }

    this.authService
      .whoAmI()
      .pipe(
        take(1),
        catchError(() => of(null)),
        switchMap((user: any) => {
          if (!user) {
            return of(null);
          }

          const id = user.remote_id ?? user.id;
          if (!id) {
            return of(null);
          }

          const employeeId = String(id);
          return this.http
            .get<any>(`${environment.baseAPIUrlMicro}egress-proxy/pne/employees/${employeeId}`)
            .pipe(
              take(1),
              map((emp) => ({ employeeId, employee: emp })),
              catchError(() => of(null)),
            );
        }),
      )
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.defaultEmployeeId = result.employeeId;
        const option = this.toEmployeeOption(result.employee, result.employeeId);
        this.employeesCache = this.mergeEmployees(this.employeesCache, [option]);
        this.employees$.next(this.employeesCache);
        this.applyAutofillDefaultsToForm();
        this.updatePayload();
        this.cd.markForCheck();
      });
  }

  private getFillAndSignMode(): boolean {
    return !this.isDesign && !this.isPreview && !this.isPrinting;
  }

  private updateDefaultDateValue(): void {
    this.defaultDateValue = this.isFillAndSignMode ? parseLocalDateString(new Date()) : '';
  }

  private isTemplateEntity(): boolean {
    const entity = this.entity as any;
    if (!entity) {
      return true;
    }

    if ('patient' in entity) {
      return !entity.patient;
    }

    return true;
  }

  private loadAttributeLabels(): void {
    if (this.isPrinting || this.isEmrReadonly || this.isRxStaffContext) {
      return;
    }

    const hasAttributes =
      this.subjectiveAttributes.length > 0 ||
      this.objectiveAttributes.length > 0 ||
      this.assessmentAttributes.length > 0 ||
      this.planAttributes.length > 0;

    if (!hasAttributes) {
      return;
    }

    if (this.areAttributeLabelsReady()) {
      return;
    }

    const cachedAttributes = this.soapNotesScribeSupport.getSoapAttributesCatalogSnapshot();
    if (cachedAttributes.length > 0) {
      this.applyAttributeLabels(cachedAttributes);
      this.cd.markForCheck();
      return;
    }

    this.soapNotesScribeSupport.ensureSoapAttributesCatalogLoaded(
      this.soapNotesService,
      (attributes) => {
        this.applyAttributeLabels(attributes);
        this.cd.markForCheck();
      },
    );
  }

  private applyAttributeLabels(attributes: SoapAttribute[]): void {
    const labelMap = new Map(attributes.map((attr) => [String(attr.id), attr.name]));
    this.subjectiveAttributeLabels = this.subjectiveAttributes.map(
      (id) => labelMap.get(String(id)) ?? String(id),
    );
    this.objectiveAttributeLabels = this.objectiveAttributes.map(
      (id) => labelMap.get(String(id)) ?? String(id),
    );
    this.assessmentAttributeLabels = this.assessmentAttributes.map(
      (id) => labelMap.get(String(id)) ?? String(id),
    );
    this.planAttributeLabels = this.planAttributes.map(
      (id) => labelMap.get(String(id)) ?? String(id),
    );
    this.attributeLabelsReady.subjective = this.subjectiveAttributes.length > 0;
    this.attributeLabelsReady.objective = this.objectiveAttributes.length > 0;
    this.attributeLabelsReady.assessment = this.assessmentAttributes.length > 0;
    this.attributeLabelsReady.plan = this.planAttributes.length > 0;
    this.updatePayload();
  }

  private formatListValue(value: any): string {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (item && typeof item === 'object') {
            return (
              item.name || item.title || item.label || item.id || item.Id || JSON.stringify(item)
            );
          }
          return String(item);
        })
        .filter((item) => item !== undefined && item !== null && String(item).trim() !== '')
        .join(', ');
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  private parseListValue(value: any): string[] {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [];
  }

  private formatScalarValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  }

  private normalizePriorityValue(value: unknown): string | number {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      if (value === -1) {
        return '';
      }
      if (value === 0 || value === 1 || value === 2) {
        return value;
      }
      return '';
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return '';
    }

    const lowered = normalized.toLowerCase();
    if (lowered === 'none') {
      return '';
    }
    if (lowered === 'low') {
      return 0;
    }
    if (lowered === 'medium') {
      return 1;
    }
    if (lowered === 'high') {
      return 2;
    }

    const asNumber = Number(normalized);
    if (!Number.isNaN(asNumber)) {
      if (asNumber === 0 || asNumber === 1 || asNumber === 2) {
        return asNumber;
      }
      return '';
    }

    return normalized;
  }

  private normalizePriorityForPayload(value: unknown): number {
    if (value === null || value === undefined || value === '') {
      return -1;
    }

    if (typeof value === 'number') {
      return value === 0 || value === 1 || value === 2 ? value : -1;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return -1;
    }

    const lowered = normalized.toLowerCase();
    if (lowered === 'low') {
      return 0;
    }
    if (lowered === 'medium') {
      return 1;
    }
    if (lowered === 'high') {
      return 2;
    }
    if (lowered === 'none') {
      return -1;
    }

    const asNumber = Number(normalized);
    if (!Number.isNaN(asNumber)) {
      return asNumber === 0 || asNumber === 1 || asNumber === 2 ? asNumber : -1;
    }

    return -1;
  }

  private normalizePlanItemsValue(planItemsValue: unknown): SoapPlanItem[] {
    if (Array.isArray(planItemsValue)) {
      return planItemsValue
        .map((item) => this.toPlanItem(item))
        .filter((item): item is SoapPlanItem => !!item);
    }

    const single = this.toPlanItem(planItemsValue);
    return single ? [single] : [];
  }

  private toPlanItem(value: unknown): SoapPlanItem | null {
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      const id = record['InvMasterId'] ?? record['plan_item_id'] ?? record['Id'];
      const inventoryShort = normalizePlanItemLabel(record['InventoryShort']);
      const itemDescription = normalizePlanItemLabel(record['ItemDescription']);
      const name = resolvePlanItemName(inventoryShort, itemDescription);
      const resolvedId = id ?? name;
      if (!resolvedId) {
        return null;
      }
      const resolvedName = name || String(resolvedId);
      const addonQty = this.normalizeAddonQty(record['AddonQty'] ?? record['quantity']);
      return {
        Id: String(resolvedId),
        InvMasterId: record['InvMasterId'] ? String(record['InvMasterId']) : undefined,
        ApptDetailId: record['ApptDetailId'] ? String(record['ApptDetailId']) : undefined,
        TreatmentId: record['TreatmentId'] ? String(record['TreatmentId']) : undefined,
        TreatmentPlanItemId: record['Id'] ? String(record['Id']) : undefined,
        ItemDescription: itemDescription || resolvedName,
        AddonQty: addonQty,
        InventoryShort: inventoryShort || undefined,
        InvType: record['InvType'] ? String(record['InvType']) : undefined,
        id: String(resolvedId),
        name: resolvedName,
      };
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const text = String(value).trim();
      if (!text) {
        return null;
      }
      return {
        Id: text,
        ItemDescription: text,
        AddonQty: null,
        id: text,
        name: text,
      };
    }

    return null;
  }

  private normalizeAddonQty(value: unknown): number | string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : trimmed;
  }

  private getPlanItemsQuantityFromMetadata(value: unknown): number[] | null {
    if (!Array.isArray(value)) {
      return null;
    }

    const quantities = value.map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }
      const record = item as Record<string, unknown>;
      return this.normalizePlanItemQuantityValue(record['quantity']);
    });

    return quantities.some((item) => item !== null) ? quantities : null;
  }

  private normalizePlanItemQuantityValue(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private ensureSelectedEmployees(items: any): void {
    if (this.isEmrReadonly || this.isRxStaffContext) {
      return;
    }

    const selectedIds = this.collectEmployeeIds(items);
    if (selectedIds.length === 0) {
      return;
    }

    const existingIds = new Set(
      (this.employees$.value || []).map((employee) => String(employee.value)),
    );
    const missingIds = selectedIds.filter((id) => !existingIds.has(String(id)));
    if (missingIds.length === 0) {
      return;
    }

    forkJoin(
      missingIds.map((employeeId) =>
        this.http
          .get<any>(`${environment.baseAPIUrlMicro}egress-proxy/pne/employees/${employeeId}`)
          .pipe(
            take(1),
            map((emp) => this.toEmployeeOption(emp, employeeId)),
            catchError(() => of(null)),
          ),
      ),
    ).subscribe((employees) => {
      const additions = employees.filter(Boolean) as Array<{ text: string; value: string }>;
      if (additions.length === 0) {
        return;
      }

      this.employeesCache = this.mergeEmployees(this.employeesCache, additions);
      this.employees$.next(this.employeesCache);
      this.cd.markForCheck();
    });
  }

  private mergeEmployeeLabels(items: any): void {
    const stored = this.getStoredEmployeeLabels(items);
    if (stored.length === 0) {
      return;
    }

    this.employeesCache = this.mergeEmployees(this.employeesCache, stored);
    this.employees$.next(this.employeesCache);
  }

  private getStoredEmployeeLabels(items: any): Array<{ text: string; value: string }> {
    if (!items || typeof items !== 'object') {
      return [];
    }

    const raw = items['employee_labels'];
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((entry) => {
        if (!entry || typeof entry !== 'object') {
          return null;
        }
        const record = entry as Record<string, unknown>;
        const value = record['value'] ?? record['id'] ?? record['Id'] ?? record['EmployeeId'];
        const text =
          record['text'] ??
          record['name'] ??
          record['label'] ??
          record['EmployeeShort'] ??
          `${record['FirstName'] || ''} ${record['LastName'] || ''}`.trim();
        const resolvedValue = value ?? text;
        if (!resolvedValue) {
          return null;
        }
        const resolvedText = text ? String(text) : String(resolvedValue);
        return { text: resolvedText, value: String(resolvedValue) };
      })
      .filter((item): item is { text: string; value: string } => !!item);
  }

  private collectEmployeeIds(items: any): string[] {
    if (!items || typeof items !== 'object') {
      return [];
    }

    const ids = new Set<string>();
    const collect = (section: any[]) => {
      if (!Array.isArray(section)) {
        return;
      }
      section.forEach((item) => {
        const raw = item?.employee ?? item?.employee_id;
        if (raw !== null && raw !== undefined && raw !== '') {
          ids.add(String(raw));
        }
      });
    };

    collect(items.subjective);
    collect(items.objective);
    collect(items.assessment);
    collect(items.plan);

    return Array.from(ids);
  }

  private buildEmployeeLabels(ids: string[]): Array<{ text: string; value: string }> {
    if (ids.length === 0) {
      return [];
    }

    const lookup = new Map(
      (this.employees$.value || []).map((item) => [String(item.value), item.text]),
    );
    const labels: Array<{ text: string; value: string }> = [];
    ids.forEach((id) => {
      const key = String(id);
      const text = lookup.get(key);
      if (text) {
        labels.push({ value: key, text });
      }
    });

    return labels;
  }

  private toEmployeeOption(
    emp: any,
    fallbackId?: string | number,
  ): { text: string; value: string } {
    const text =
      emp?.EmployeeShort ||
      `${emp?.FirstName || ''} ${emp?.LastName || ''}`.trim() ||
      emp?.Name ||
      '';
    const value = emp?.Id ?? emp?.EmployeeId ?? fallbackId ?? '';
    return { text: text || String(value), value: String(value) };
  }

  private mergeEmployees(
    current: Array<{ text: string; value: string }>,
    additions: Array<{ text: string; value: string }>,
  ): Array<{ text: string; value: string }> {
    if (additions.length === 0) {
      return current;
    }

    const seen = new Set(current.map((item) => String(item.value)));
    const merged = [...current];

    additions.forEach((item) => {
      const key = String(item.value);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    });

    return merged;
  }

  public getMetadata(): Record<string, unknown> {
    if (this.payload && this.payload.properties) {
      const widgetData = {
        ...this.payload,
        properties: { ...this.payload.properties },
      };
      const widget = new FbEmrSoapNotesWidget(widgetData);
      return widget.getMetadata();
    }

    return {};
  }

  private applyCurrentDateToChangedNotesInForm(): void {
    if (this.isApplyingDateUpdate) {
      return;
    }

    const metadata = this.getSoapNotesFromMetadata();
    const today = parseLocalDateString(new Date());
    const sections: Array<'subjective' | 'objective' | 'assessment' | 'plan'> = [
      'subjective',
      'objective',
      'assessment',
      'plan',
    ];

    sections.forEach((section) => {
      const control = this.soapForm.get(section);
      const rows = control?.value as SoapInputRow[];
      if (!control || !Array.isArray(rows) || rows.length === 0) {
        return;
      }
      const hasSectionAttributes = this.getAssignedAttributes(section).length > 0;
      const hasPlanSectionData =
        section === 'plan' &&
        (this.planItems.length > 0 ||
          (this.planItemsQuantity !== null &&
            this.planItemsQuantity !== undefined &&
            this.planItemsQuantity !== ''));
      const sectionMetadataChanged =
        this.haveSectionAttributesChanged(section, metadata) ||
        (section === 'plan' && this.hasPlanSectionDataChanged(metadata));

      const metadataSection = Array.isArray(metadata?.[section])
        ? (metadata?.[section] as any[])
        : [];
      const metadataById = this.buildMetadataIndex(metadataSection);

      let changed = false;
      const nextRows = rows.map((row, index) => {
        if (!row || !Array.isArray(row.fields)) {
          return row;
        }

        const rowId = row.id ?? (row as any).Id ?? (row as any).ID;
        const noteText = this.getRowNoteText(row);
        const priority = this.normalizePriorityForPayload(this.getRowPriorityValue(row));
        const hasContent =
          this.isMeaningfulValue(noteText) ||
          priority !== -1 ||
          hasSectionAttributes ||
          hasPlanSectionData;

        if (!hasContent) {
          return row;
        }

        const previous = this.getPreviousRecord(rowId, index, metadataSection, metadataById);

        if (!this.shouldUpdateNoteDate(noteText, priority, previous, sectionMetadataChanged)) {
          return row;
        }

        const updated = this.setRowDate(row, today);
        if (updated !== row) {
          changed = true;
        }
        return updated;
      });

      if (changed) {
        this.isApplyingDateUpdate = true;
        control.setValue(nextRows, { emitEvent: false });
        this.updatePayload();
        this.cd.markForCheck();
        this.isApplyingDateUpdate = false;
      }
    });
  }

  private buildMetadataIndex(section: any[]): Map<string, Record<string, unknown>> {
    const metadataById = new Map<string, Record<string, unknown>>();
    section.forEach((item) => {
      if (!item || typeof item !== 'object') {
        return;
      }
      const record = item as Record<string, unknown>;
      const id = record['id'] ?? record['Id'] ?? record['ID'];
      if (id !== null && id !== undefined && id !== '') {
        metadataById.set(String(id), record);
      }
    });
    return metadataById;
  }

  private getPreviousRecord(
    id: unknown,
    index: number,
    metadataSection: any[],
    metadataById: Map<string, Record<string, unknown>>,
  ): Record<string, unknown> | undefined {
    if (id !== null && id !== undefined && id !== '') {
      return metadataById.get(String(id));
    }
    const fallback = metadataSection[index];
    return fallback && typeof fallback === 'object'
      ? (fallback as Record<string, unknown>)
      : undefined;
  }

  private shouldUpdateNoteDate(
    noteText: string,
    priority: number,
    previous?: Record<string, unknown>,
    forceUpdate = false,
  ): boolean {
    if (forceUpdate) {
      return true;
    }

    if (!previous) {
      return true;
    }

    const prevNoteText = this.normalizeNoteText(previous);
    const prevPriority = this.normalizePriorityForPayload(
      previous['priority'] ?? previous['Priority'] ?? previous['PriorityLevel'],
    );

    return noteText !== prevNoteText || priority !== prevPriority;
  }

  private haveSectionAttributesChanged(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
    metadata: Record<string, unknown> | null,
  ): boolean {
    const current = this.getAssignedAttributes(section)
      .map((item) => String(item))
      .sort();
    const previous = this.getSectionAttributes(metadata, section)
      .map((item) => String(item))
      .sort();
    return JSON.stringify(current) !== JSON.stringify(previous);
  }

  private hasPlanSectionDataChanged(metadata: Record<string, unknown> | null): boolean {
    const currentPlanItems = this.normalizePlanItemsSnapshot(this.planItems as unknown[]);
    const previousPlanItems = this.normalizePlanItemsSnapshot(this.getSectionPlanItems(metadata));
    if (JSON.stringify(currentPlanItems) !== JSON.stringify(previousPlanItems)) {
      return true;
    }

    const currentQuantity = this.normalizePlanItemsQuantitySnapshot(this.planItemsQuantity);
    const previousQuantity = this.normalizePlanItemsQuantitySnapshot(
      this.getSectionPlanItemsQuantity(metadata),
    );

    return JSON.stringify(currentQuantity) !== JSON.stringify(previousQuantity);
  }

  private normalizePlanItemsSnapshot(raw: unknown[]): string[] {
    if (!Array.isArray(raw) || raw.length === 0) {
      return [];
    }

    return raw
      .map((item) => {
        if (!item || typeof item !== 'object') {
          const text = String(item ?? '').trim();
          return text ? `${text}|` : '';
        }

        const record = item as Record<string, unknown>;
        const id =
          record['InvMasterId'] ??
          record['plan_item_id'] ??
          record['Id'] ??
          record['id'] ??
          record['ItemDescription'] ??
          record['name'];
        const qty = this.normalizePlanItemQuantityValue(record['AddonQty'] ?? record['quantity']);
        const idText = String(id ?? '').trim();
        return idText ? `${idText}|${qty ?? ''}` : '';
      })
      .filter((item) => item !== '')
      .sort();
  }

  private normalizePlanItemsQuantitySnapshot(value: unknown): Array<number | null> {
    if (Array.isArray(value)) {
      return value.map((item) => this.normalizePlanItemQuantityValue(item));
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return [this.normalizePlanItemQuantityValue(value)];
  }

  private normalizeNoteText(record: Record<string, unknown>): string {
    const note =
      record['note'] ?? record['Note'] ?? record['notes'] ?? record['Notes'] ?? record['noteText'];
    if (note === null || note === undefined) {
      return '';
    }
    return String(note).trim();
  }

  private getRowNoteText(row: SoapInputRow): string {
    const field = row.fields.find((item) => item.type === 'textarea');
    if (!field || field.value === null || field.value === undefined) {
      return '';
    }
    return String(field.value).trim();
  }

  private getRowPriorityValue(row: SoapInputRow): unknown {
    const field = row.fields.find((item) => item.type === 'priority');
    return field?.value;
  }

  private setRowDate(row: SoapInputRow, date: string): SoapInputRow {
    let updated = false;
    const fields = row.fields.map((field) => {
      if (field.type !== 'date') {
        return field;
      }
      if (field.value === date) {
        return field;
      }
      updated = true;
      return { ...field, value: date };
    });
    if (!updated) {
      return row;
    }
    return { ...row, fields };
  }

  getValue() {
    const formValue = this.soapForm.getRawValue();
    const nextItems: Record<string, unknown> = {
      subjective: this.formatDataForPayload(formValue.subjective || [], this.subjectiveAttributes),
      objective: this.formatDataForPayload(formValue.objective || [], this.objectiveAttributes),
      assessment: this.formatDataForPayload(formValue.assessment || [], this.assessmentAttributes),
      plan: this.formatDataForPayload(
        formValue.plan || [],
        this.planAttributes,
        this.planItems,
        this.planItemsQuantity,
      ),
      subjective_attributes: [...this.subjectiveAttributes],
      objective_attributes: [...this.objectiveAttributes],
      assessment_attributes: [...this.assessmentAttributes],
      plan_attributes: [...this.planAttributes],
      ...(this.attributeLabelsReady.subjective && this.subjectiveAttributeLabels.length
        ? { subjective_attribute_labels: [...this.subjectiveAttributeLabels] }
        : {}),
      ...(this.attributeLabelsReady.objective && this.objectiveAttributeLabels.length
        ? { objective_attribute_labels: [...this.objectiveAttributeLabels] }
        : {}),
      ...(this.attributeLabelsReady.assessment && this.assessmentAttributeLabels.length
        ? { assessment_attribute_labels: [...this.assessmentAttributeLabels] }
        : {}),
      ...(this.attributeLabelsReady.plan && this.planAttributeLabels.length
        ? { plan_attribute_labels: [...this.planAttributeLabels] }
        : {}),
      plan_items: [...this.planItems],
      plan_items_quantity: this.planItemsQuantity,
    };

    const selectedEmployeeIds = this.collectEmployeeIds(nextItems);
    const storedLabels = this.getStoredEmployeeLabels(this.payload?.properties?.items?.value);
    const labelMap = new Map(storedLabels.map((item) => [item.value, item.text]));
    const resolvedLabels = this.buildEmployeeLabels(selectedEmployeeIds);
    resolvedLabels.forEach((item) => labelMap.set(item.value, item.text));
    const employeeLabels = selectedEmployeeIds
      .map((id) => {
        const key = String(id);
        const text = labelMap.get(key);
        return text ? { value: key, text } : null;
      })
      .filter((item): item is { text: string; value: string } => !!item);
    if (employeeLabels.length > 0) {
      nextItems['employee_labels'] = employeeLabels;
    }

    copyScribeSuggestionMarker(this.soapMarkerTarget, nextItems);
    return nextItems;
  }

  public applySectionSuggestion(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
    patch: { note?: string; priority?: number | null },
  ): void {
    const currentRows = this.getSectionValue(section);
    const targetRowIndex = currentRows.findIndex((row) =>
      row?.fields?.some(
        (field) => field.type === 'textarea' && (!field.value || `${field.value}`.trim() === ''),
      ),
    );

    const nextRows =
      targetRowIndex >= 0
        ? currentRows.map((row, index) =>
            index === targetRowIndex ? this.withUpdatedSectionPatch(row, patch) : row,
          )
        : [
            ...currentRows,
            this.withUpdatedSectionPatch(
              this.applyAutofillDefaults([
                this.createEmptyRow(this.getFieldsForSection(section)),
              ])[0],
              patch,
            ),
          ];

    this.soapForm.patchValue({ [section]: nextRows }, { emitEvent: false });
    this.soapForm.get(section)?.markAsDirty();
    this.soapForm.markAsDirty();
    this.updatePayload();
    this.sectionExpandedOverrides[section] = true;
    this.cd.markForCheck();
  }

  private getSectionValue(section: keyof SoapNotesPayload): SoapInputRow[] {
    return (this.soapForm.get(section)?.value as SoapInputRow[]) || [];
  }

  private getFieldsForSection(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
  ): InputField[] {
    if (section === 'subjective') {
      return this.subjectiveFields;
    }
    if (section === 'objective') {
      return this.objectiveFields;
    }
    if (section === 'assessment') {
      return this.assessmentFields;
    }
    return this.planFields;
  }

  private withUpdatedSectionPatch(
    row: SoapInputRow,
    patch: { note?: string; priority?: number | null },
  ): SoapInputRow {
    return {
      ...row,
      fields: row.fields.map((field) => {
        if (field.type === 'textarea' && patch.note !== undefined) {
          return { ...field, value: patch.note };
        }
        if (field.type === 'priority' && patch.priority !== undefined) {
          return { ...field, value: patch.priority ?? '' };
        }
        return field;
      }),
    };
  }

  private ensureSectionValue(value: SoapInputRow[], fields: InputField[]): SoapInputRow[] {
    return value.length > 0 ? value : [this.createEmptyRow(fields)];
  }

  private hasExistingSoapNotes(): boolean {
    const items = this.payload?.properties?.items?.value;
    const hasMetadata = this.hasSoapNotesMetadata();
    const isTemplate = this.isTemplateEntity();

    if (!items || typeof items !== 'object') {
      return hasMetadata;
    }

    if (!isTemplate && !hasMetadata) {
      return false;
    }

    return (
      this.sectionHasData(items.subjective) ||
      this.sectionHasData(items.objective) ||
      this.sectionHasData(items.assessment) ||
      this.sectionHasData(items.plan) ||
      this.getSectionAttributes(items, 'subjective').length > 0 ||
      this.getSectionAttributes(items, 'objective').length > 0 ||
      this.getSectionAttributes(items, 'assessment').length > 0 ||
      this.getSectionAttributes(items, 'plan').length > 0 ||
      this.getSectionPlanItems(items).length > 0 ||
      this.getSectionPlanItemsQuantity(items) !== null ||
      hasMetadata
    );
  }

  private sectionHasData(section: any): boolean {
    if (!Array.isArray(section)) {
      return false;
    }

    return section.some((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }

      const record = item as Record<string, unknown>;
      return this.hasMeaningfulSoapData(record);
    });
  }

  private hasMeaningfulSoapData(record: Record<string, unknown>): boolean {
    const note =
      record['note'] ?? record['Note'] ?? record['notes'] ?? record['Notes'] ?? record['noteText'];
    if (this.isMeaningfulValue(note)) {
      return true;
    }

    const priority = this.normalizePriorityValue(
      record['priority'] ?? record['Priority'] ?? record['PriorityLevel'],
    );
    if (this.isMeaningfulValue(priority)) {
      return true;
    }

    const planFields = record['plan_fields'] ?? record['planFields'];
    if (this.isMeaningfulValue(planFields)) {
      return true;
    }

    const planItems = record['plan_items'] ?? record['planItems'];
    if (this.isMeaningfulValue(planItems)) {
      return true;
    }

    const planItemsQuantity = record['plan_items_quantity'] ?? record['planItemsQuantity'];
    if (this.isMeaningfulValue(planItemsQuantity)) {
      return true;
    }

    return false;
  }

  private hasRowMeaningfulContent(row: SoapInputRow): boolean {
    if (!row || !Array.isArray(row.fields)) {
      return false;
    }

    return row.fields.some((field) => {
      if (!field || field.type === 'date' || field.type === 'employee') {
        return false;
      }
      const value = field.value;
      if (value === null || value === undefined) {
        return false;
      }
      if (field.type === 'priority') {
        return this.normalizePriorityForPayload(value) !== -1;
      }
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      return value !== null && value !== undefined;
    });
  }

  private isDefaultAutofillField(field: InputField | undefined, value: unknown): boolean {
    if (!field) {
      return false;
    }
    if (field.type === 'date' && this.defaultDateValue) {
      return String(value ?? '') === String(this.defaultDateValue);
    }
    if (field.type === 'employee' && this.defaultEmployeeId) {
      return String(value ?? '') === String(this.defaultEmployeeId);
    }
    return false;
  }

  private hasSoapNotesMetadata(): boolean {
    const metadata = this.entity?.metadata?.soap_notes ?? this.entity?.metadata?.soapNotes ?? null;

    if (!metadata || typeof metadata !== 'object') {
      return false;
    }

    const record = metadata as Record<string, unknown>;
    const sections = ['subjective_notes', 'objective_notes', 'assessment_notes', 'plan_notes'];

    for (const key of sections) {
      const section = record[key];
      if (Array.isArray(section) && section.some((item) => this.hasSoapNoteMetadataItem(item))) {
        return true;
      }
    }

    const attributesKeys = [
      'subjective_notes_attributes',
      'objective_notes_attributes',
      'assessment_notes_attributes',
      'plan_notes_attributes',
    ];
    if (
      attributesKeys.some(
        (key) => Array.isArray(record[key]) && (record[key] as unknown[]).length > 0,
      )
    ) {
      return true;
    }

    const planItems = record['plan_items'] ?? record['planItems'];
    if (Array.isArray(planItems) && planItems.length > 0) {
      return true;
    }

    return false;
  }

  private hasSoapNoteMetadataItem(item: unknown): boolean {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const record = item as Record<string, unknown>;
    const note =
      record['note'] ?? record['Note'] ?? record['notes'] ?? record['Notes'] ?? record['noteText'];
    if (this.isMeaningfulValue(note)) {
      return true;
    }

    const priority = this.normalizePriorityValue(
      record['priority'] ?? record['Priority'] ?? record['PriorityLevel'],
    );
    if (this.isMeaningfulValue(priority)) {
      return true;
    }

    return false;
  }

  private getAssignedAttributes(
    section: 'subjective' | 'objective' | 'assessment' | 'plan',
  ): string[] {
    if (section === 'subjective') {
      return this.subjectiveAttributes;
    }
    if (section === 'objective') {
      return this.objectiveAttributes;
    }
    if (section === 'assessment') {
      return this.assessmentAttributes;
    }
    return this.planAttributes;
  }

  private isMeaningfulValue(value: unknown): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    if (typeof value === 'number') {
      return !Number.isNaN(value);
    }

    return true;
  }

  private applySoapNotesPayload(
    payload: SoapNotesPayload,
    attributes?: SoapAttributesByCategory,
    planItems?: SoapPlanItem[],
  ) {
    if (!this.payload?.properties) {
      return;
    }

    if (!this.payload.properties.items) {
      this.payload.properties.items = { value: {} };
    }

    this.payload.properties.items.value = {
      subjective: payload.subjective,
      objective: payload.objective,
      assessment: payload.assessment,
      plan: payload.plan,
      ...(attributes?.subjective?.length ? { subjective_attributes: attributes.subjective } : {}),
      ...(attributes?.objective?.length ? { objective_attributes: attributes.objective } : {}),
      ...(attributes?.assessment?.length ? { assessment_attributes: attributes.assessment } : {}),
      ...(attributes?.plan?.length ? { plan_attributes: attributes.plan } : {}),
      ...(planItems?.length ? { plan_items: planItems } : {}),
      ...(planItems?.length
        ? {
            plan_items_quantity: planItems.map((item) => this.normalizePlanItemQuantity(item)),
          }
        : {}),
    };

    this.initializeData();
  }

  private applyPlanItems(planItems: SoapPlanItem[]): void {
    if (!this.payload?.properties) {
      return;
    }

    if (!this.payload.properties.items) {
      this.payload.properties.items = { value: {} };
    }

    this.planItems = planItems;
    this.planItemsQuantity = planItems.map((item) => this.normalizePlanItemQuantity(item));
    const items = this.payload.properties.items.value ?? {};
    this.payload.properties.items.value = {
      ...items,
      plan_items: [...this.planItems],
      plan_items_quantity: this.planItemsQuantity,
    };
    this.initializeData();
  }

  private ensureSoapAttributesCatalogLoaded(): void {
    this.soapNotesScribeSupport.ensureSoapAttributesCatalogLoaded(
      this.soapNotesService,
      (attributes) => {
        if (this.hasAnyAssignedAttributes()) {
          this.applyAttributeLabels(attributes);
        }

        this.cd.markForCheck();
      },
    );
  }

  private ensureSoapPlanItemsCatalogLoaded(): void {
    this.soapNotesScribeSupport.ensureSoapPlanItemsCatalogLoaded(
      this.soapNotesService,
      (catalog) => {
        this.applyPlanItemCatalogLabels(catalog);
        this.cd.markForCheck();
      },
    );
  }

  /**
   * Plan items returned by PNE for a treatment, and items persisted before the Service Id label
   * was introduced, only carry ItemDescription. Once the catalog is available, re-label them by
   * InvMasterId so the chart shows the same "Service Id" as Simple SOAP.
   */
  private applyPlanItemCatalogLabels(catalog: SoapPlanItem[]): void {
    const relabeled = this.withCatalogLabels(this.planItems, catalog);
    const changed = relabeled.some((item, index) => item !== this.planItems[index]);
    if (!changed) {
      return;
    }

    this.planItems = relabeled;
    this.updatePayload();
  }

  private withCatalogLabels(
    planItems: SoapPlanItem[],
    catalog: SoapPlanItem[] = this.soapNotesScribeSupport.getSoapPlanItemsCatalogSnapshot(),
  ): SoapPlanItem[] {
    if (!planItems.length || !catalog.length) {
      return planItems;
    }

    const catalogById = new Map(
      catalog.map((item) => [String(item.InvMasterId ?? item.id), item] as const),
    );

    return planItems.map((item) => {
      const match = catalogById.get(String(item.InvMasterId ?? item.id));
      const inventoryShort = normalizePlanItemLabel(match?.InventoryShort);
      if (!match || !inventoryShort) {
        return item;
      }
      if (item.InventoryShort === inventoryShort && item.name === inventoryShort) {
        return item;
      }

      return {
        ...item,
        InventoryShort: inventoryShort,
        InvType: item.InvType ?? match.InvType,
        name: inventoryShort,
      };
    });
  }

  private hasAnyAssignedAttributes(): boolean {
    return (
      this.subjectiveAttributes.length > 0 ||
      this.objectiveAttributes.length > 0 ||
      this.assessmentAttributes.length > 0 ||
      this.planAttributes.length > 0
    );
  }

  private normalizePlanItemQuantity(item: SoapPlanItem): number | null {
    const raw = item.AddonQty;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }

    if (typeof raw === 'number') {
      return Number.isFinite(raw) ? raw : null;
    }

    const trimmed = String(raw).trim();
    if (!trimmed) {
      return null;
    }

    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private emptySoapAttributes(): SoapAttributesByCategory {
    return { subjective: [], objective: [], assessment: [], plan: [] };
  }

  private hasSoapAttributesData(attributes?: SoapAttributesByCategory): boolean {
    if (!attributes) {
      return false;
    }

    return (
      attributes.subjective.length > 0 ||
      attributes.objective.length > 0 ||
      attributes.assessment.length > 0 ||
      attributes.plan.length > 0
    );
  }

  private hasSoapNotesData(payload: SoapNotesPayload): boolean {
    return (
      payload.subjective.length > 0 ||
      payload.objective.length > 0 ||
      payload.assessment.length > 0 ||
      payload.plan.length > 0
    );
  }

  private getSectionAttributes(items: any, key: string): string[] {
    if (!items || typeof items !== 'object') {
      return [];
    }

    const directAttributes = items[`${key}_attributes`] ?? items[`${key}_assigned_attributes`];
    const normalizedDirect = this.normalizeAttributes(directAttributes);
    if (normalizedDirect.length > 0) {
      return normalizedDirect;
    }

    const section = items[key];
    if (Array.isArray(section)) {
      for (const item of section) {
        const normalized = this.normalizeAttributes(item?.assigned_attributes);
        if (normalized.length > 0) {
          return normalized;
        }
      }
    }

    return [];
  }

  private getStoredAttributeLabels(
    items: any,
    key: 'subjective' | 'objective' | 'assessment' | 'plan',
  ): { labels: string[] | null; hasStored: boolean } {
    if (!items || typeof items !== 'object') {
      return { labels: null, hasStored: false };
    }

    const raw = items[`${key}_attribute_labels`];
    const normalized = this.normalizeAttributes(raw);
    if (normalized.length > 0) {
      return { labels: normalized, hasStored: true };
    }

    return { labels: null, hasStored: false };
  }

  private areAttributeLabelsReady(): boolean {
    const subjectiveReady =
      this.subjectiveAttributes.length === 0 || this.attributeLabelsReady.subjective;
    const objectiveReady =
      this.objectiveAttributes.length === 0 || this.attributeLabelsReady.objective;
    const assessmentReady =
      this.assessmentAttributes.length === 0 || this.attributeLabelsReady.assessment;
    const planReady = this.planAttributes.length === 0 || this.attributeLabelsReady.plan;
    return subjectiveReady && objectiveReady && assessmentReady && planReady;
  }

  private shouldFetchPlanItems(items: any): boolean {
    if (!items || typeof items !== 'object') {
      return false;
    }

    const direct = items['plan_items'] ?? items['planItems'];
    if (Array.isArray(direct) && direct.length > 0) {
      return !this.planItemsHaveLabels(direct);
    }

    const section = Array.isArray(items['plan']) ? items['plan'] : [];
    const collected: unknown[] = [];
    section.forEach((entry) => {
      if (!entry || typeof entry !== 'object') {
        return;
      }
      const record = entry as Record<string, unknown>;
      const rowItems = record['plan_items'] ?? record['planItems'];
      if (Array.isArray(rowItems)) {
        collected.push(...rowItems);
      }
    });

    if (collected.length === 0) {
      return false;
    }

    return !this.planItemsHaveLabels(collected);
  }

  private planItemsHaveLabels(raw: unknown[]): boolean {
    return raw.every((item) => {
      if (!item || typeof item !== 'object') {
        return false;
      }
      const record = item as Record<string, unknown>;
      const label =
        record['ItemDescription'] ?? record['name'] ?? record['title'] ?? record['label'];
      return !!label;
    });
  }

  private getSectionPlanItems(items: any): SoapPlanItem[] {
    if (!items || typeof items !== 'object') {
      return [];
    }

    const directItems = items['plan_items'];
    const normalizedDirect = this.normalizePlanItemsValue(directItems);
    if (normalizedDirect.length > 0) {
      return normalizedDirect;
    }

    const section = items['plan'];
    if (Array.isArray(section)) {
      for (const item of section) {
        const planItems = item?.plan_items;
        const normalized = this.normalizePlanItemsValue(planItems);
        if (normalized.length > 0) {
          return normalized;
        }
      }
    }

    return [];
  }

  private getSectionPlanItemsQuantity(items: any): unknown[] | number | string | null {
    if (!items || typeof items !== 'object') {
      return null;
    }

    const directQuantity = items['plan_items_quantity'];
    if (directQuantity !== null && directQuantity !== undefined && directQuantity !== '') {
      return directQuantity;
    }

    const section = items['plan'];
    if (Array.isArray(section)) {
      for (const item of section) {
        const quantity = item?.plan_items_quantity;
        if (quantity !== null && quantity !== undefined && quantity !== '') {
          return quantity;
        }
      }
    }

    return null;
  }

  private emptySoapNotes(): SoapNotesPayload {
    return {
      subjective: [],
      objective: [],
      assessment: [],
      plan: [],
    };
  }
}
